
import { useState, useEffect } from 'react';
import {  
  BreakpointKey, 
  ResponsiveValue,
  responsiveService 
} from 'utils/responsive';


export function useResponsive() {
  const [currentBreakpoint, setCurrentBreakpoint] = useState<BreakpointKey>(
    responsiveService.getCurrentBreakpoint()
  );

  useEffect(() => {
    const unsubscribe = responsiveService.subscribe((breakpoint) => {
      setCurrentBreakpoint(breakpoint);
    });

    return unsubscribe;
  }, []);

  return {
    currentBreakpoint,
    isMobile: responsiveService.isMobile(),
    isTablet: responsiveService.isTablet(),
    isDesktop: responsiveService.isDesktop(),
    isBreakpointUp: (breakpoint: BreakpointKey) => responsiveService.isBreakpointUp(breakpoint),
    isBreakpointDown: (breakpoint: BreakpointKey) => responsiveService.isBreakpointDown(breakpoint),
    isBreakpointBetween: (min: BreakpointKey, max: BreakpointKey) => 
      responsiveService.isBreakpointBetween(min, max),
    resolveValue: <T>(value: ResponsiveValue<T>) => responsiveService.resolveResponsiveValue(value),
    getResponsiveClasses: <T extends string>(prefix: string, values: ResponsiveValue<T>) =>
      responsiveService.getResponsiveClasses(prefix, values)
  };
}

export function useBreakpoint(breakpoint: BreakpointKey, direction: 'up' | 'down' | 'only' = 'up') {
  const [matches, setMatches] = useState(() => {
    switch (direction) {
      case 'up':
        return responsiveService.isBreakpointUp(breakpoint);
      case 'down':
        return responsiveService.isBreakpointDown(breakpoint);
      case 'only':
        return responsiveService.getCurrentBreakpoint() === breakpoint;
      default:
        return false;
    }
  });

  useEffect(() => {
    const unsubscribe = responsiveService.subscribe(() => {
      switch (direction) {
        case 'up':
          setMatches(responsiveService.isBreakpointUp(breakpoint));
          break;
        case 'down':
          setMatches(responsiveService.isBreakpointDown(breakpoint));
          break;
        case 'only':
          setMatches(responsiveService.getCurrentBreakpoint() === breakpoint);
          break;
      }
    });

    return unsubscribe;
  }, [breakpoint, direction]);

  return matches;
}

/**
 * Hook para valores responsive dinámicos
 */
export function useResponsiveValue<T>(value: ResponsiveValue<T>): T {
  const [resolvedValue, setResolvedValue] = useState(() => 
    responsiveService.resolveResponsiveValue(value)
  );

  useEffect(() => {
    const unsubscribe = responsiveService.subscribe(() => {
      setResolvedValue(responsiveService.resolveResponsiveValue(value));
    });

    return unsubscribe;
  }, [value]);

  return resolvedValue;
}


export function useDeviceType() {
  const [deviceType, setDeviceType] = useState<'mobile' | 'tablet' | 'desktop'>(() => {
    if (responsiveService.isMobile()) return 'mobile';
    if (responsiveService.isTablet()) return 'tablet';
    return 'desktop';
  });

  useEffect(() => {
    const unsubscribe = responsiveService.subscribe(() => {
      if (responsiveService.isMobile()) {
        setDeviceType('mobile');
      } else if (responsiveService.isTablet()) {
        setDeviceType('tablet');
      } else {
        setDeviceType('desktop');
      }
    });

    return unsubscribe;
  }, []);

  return {
    deviceType,
    isMobile: deviceType === 'mobile',
    isTablet: deviceType === 'tablet',
    isDesktop: deviceType === 'desktop'
  };
}


export function useResponsiveSpacing() {
  const { resolveValue } = useResponsive();

  return {
    getSpacing: (value: ResponsiveValue<string>) => resolveValue(value),
    getPadding: (value: ResponsiveValue<string>) => ({ padding: resolveValue(value) }),
    getMargin: (value: ResponsiveValue<string>) => ({ margin: resolveValue(value) }),
    getPaddingX: (value: ResponsiveValue<string>) => ({ 
      paddingLeft: resolveValue(value), 
      paddingRight: resolveValue(value) 
    }),
    getPaddingY: (value: ResponsiveValue<string>) => ({ 
      paddingTop: resolveValue(value), 
      paddingBottom: resolveValue(value) 
    }),
    getMarginX: (value: ResponsiveValue<string>) => ({ 
      marginLeft: resolveValue(value), 
      marginRight: resolveValue(value) 
    }),
    getMarginY: (value: ResponsiveValue<string>) => ({ 
      marginTop: resolveValue(value), 
      marginBottom: resolveValue(value) 
    }),
  };
}


export function useResponsiveContainer(config?: {
  maxWidth?: ResponsiveValue<string>;
  padding?: ResponsiveValue<string>;
  margin?: ResponsiveValue<string>;
}) {
  const { resolveValue } = useResponsive();

  const containerStyles = {
    maxWidth: config?.maxWidth ? resolveValue(config.maxWidth) : '100%',
    padding: config?.padding ? resolveValue(config.padding) : '1rem',
    margin: config?.margin ? resolveValue(config.margin) : '0 auto'
  };

  return {
    containerStyles,
    containerProps: {
      style: containerStyles
    }
  };
}